import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { ShieldCheck, Eye, Pencil, EyeOff, Check, Loader2, Lock, Search, ChevronDown, X, Layers } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGES = ["PO Received", "Procurement", "Manufacturing", "Quality Control", "Dispatch", "Completed"];
const STAGE_COLORS = {
  "PO Received":    "#f97316",
  "Procurement":    "#f59e0b",
  "Manufacturing":  "#8b5cf6",
  "Quality Control":"#14b8a6",
  "Dispatch":       "#f97316",
  "Completed":      "#22c55e",
};

const FEATURE_PERMISSIONS = [
  { key: "can_view_value", label: "View Financial Values", description: "Can see USD/INR amounts" },
  { key: "can_view_spend", label: "View Procurement Spend", description: "Can see material/tooling/gauging spend amounts" },
  { key: "can_edit_items",  label: "Edit Item Details",     description: "Can edit fields in the item lightbox (priority, description, dates, etc.)" },
  { key: "can_delete",     label: "Delete Items",          description: "Can delete line items" },
  { key: "can_import",     label: "Import CSV",            description: "Can bulk-import via CSV" },
  { key: "can_export",     label: "Export CSV",            description: "Can export data to CSV" },
];

const STORES_PERMISSIONS = [
  { key: "can_view_stores",     label: "View Stores",     description: "See the Stores page and stock lots" },
  { key: "can_manage_stock",    label: "Manage Stock",    description: "Receive, edit, adjust, scrap stock lots" },
  { key: "can_reserve_stock",   label: "Reserve Stock",   description: "Reserve / release stock against line items" },
  { key: "can_issue_stock",     label: "Issue Stock",     description: "Issue reserved stock to manufacturing" },
  { key: "can_view_stock_cost", label: "View Stock Cost", description: "See per-lot cost values in Stores" },
];

const MACHINES_PERMISSIONS = [
  { key: "can_view_machines", label: "View Machine Monitor", description: "Open the full-screen shop-floor / chairman machine view" },
];

// The Blocked Board (v2.4) — its own key so a manager can chase blocks
// without Admin rights. Backfilled on startup: Admin on, everyone else off.
const BLOCKED_BOARD_PERMISSIONS = [
  { key: "can_view_blocked_board", label: "View Blocked Board", description: "Open the Blocked Board and see who every block is waiting on" },
];

const MAINTENANCE_PERMISSIONS = [
  { key: "can_view_maintenance",  label: "View Maintenance",  description: "Open the Maintenance module and its statistics" },
  { key: "can_report_breakdown",  label: "Report Breakdown",  description: "Report a machine down from the shop floor" },
  { key: "can_repair_machines",   label: "Repair Machines",   description: "Take up, pause and mark repairs done" },
  { key: "can_verify_repairs",    label: "Confirm Handback",  description: "Sign a repaired machine back (or send it back)" },
  { key: "can_admin_maintenance", label: "Admin Maintenance", description: "Set per-machine downtime rates and correct records" },
];

const STOCK_CENTER_PERMISSIONS = [
  { key: "can_view_stock_center", label: "View Stock Center",   description: "Open the Rockpecker Drilling warehouse portal" },
  { key: "can_manage_bits",       label: "Manage Bits",         description: "Receive, edit, duplicate, delete bits; resolve review flags" },
  { key: "can_sell_bits",         label: "Sell Bits",           description: "Mark a bit sold / allocate to a customer" },
  { key: "can_transfer_bits",     label: "Transfer Bits",       description: "Move a bit between warehouses" },
  { key: "can_view_bit_cost",     label: "View Bit Cost",       description: "See per-bit price and stock value" },
];

const SALES_PERMISSIONS = [
  { key: "can_view_scrap_sales",   label: "View Scrap Sales",   description: "See the Scrap Sales ledger (Sales area)" },
  { key: "can_manage_scrap_sales", label: "Manage Scrap Sales", description: "Record, edit and delete scrap sale entries" },
];

const ECOTILE_PERMISSIONS = [
  { key: "can_view_ecotile",            label: "View Ecotile",    description: "Open the Ecotile flooring portal (orders, payments, targets)" },
  { key: "can_manage_ecotile_orders",   label: "Manage Orders",   description: "Add, edit and delete Ecotile orders" },
  { key: "can_record_ecotile_payments", label: "Record Payments", description: "Log payments against Ecotile orders" },
  { key: "can_view_ecotile_value",      label: "View Values",     description: "See order values, invoiced amounts and payments" },
];

// Must mirror WAREHOUSES in backend/server.py (code + display name).
const WAREHOUSES = [
  { code: "UK",  name: "United Kingdom" },
  { code: "AUS", name: "Australia" },
  { code: "CH",  name: "Switzerland" },
  { code: "CA",  name: "Canada" },
  { code: "IN",  name: "India" },
];

// Must mirror DIVISIONS in backend/server.py (key + label).
const DIVISIONS = [
  { key: "metallic", label: "Metallic" },
  { key: "drilling", label: "Drilling" },
  { key: "ecotile",  label: "Ecotile" },
];

const ACCESS_LEVELS = [
  { value: "none", label: "None",  Icon: EyeOff,      cls: "text-[hsl(var(--muted-foreground))]" },
  { value: "view", label: "View",  Icon: Eye,          cls: "text-blue-500" },
  { value: "edit", label: "Edit",  Icon: Pencil,       cls: "text-emerald-500" },
];

export default function UserPermissionsModal({ user, token, onClose, onSaved }) {
  const [perms, setPerms] = useState(() => ({
    stages: { ...({ "PO Received":"view","Procurement":"view","Manufacturing":"view","Quality Control":"view","Dispatch":"view","Completed":"view" }), ...user.permissions?.stages },
    can_view_value: user.permissions?.can_view_value ?? false,
    can_view_spend: user.permissions?.can_view_spend ?? true,
    can_edit_items:  user.permissions?.can_edit_items  ?? true,
    can_delete:     user.permissions?.can_delete     ?? false,
    can_import:     user.permissions?.can_import     ?? false,
    can_export:     user.permissions?.can_export     ?? true,
    can_view_stores:     user.permissions?.can_view_stores     ?? false,
    can_manage_stock:    user.permissions?.can_manage_stock    ?? false,
    can_reserve_stock:   user.permissions?.can_reserve_stock   ?? false,
    can_issue_stock:     user.permissions?.can_issue_stock     ?? false,
    can_view_stock_cost: user.permissions?.can_view_stock_cost ?? false,
    can_view_machines:   user.permissions?.can_view_machines   ?? false,
    can_view_blocked_board: user.permissions?.can_view_blocked_board ?? false,
    can_view_maintenance:  user.permissions?.can_view_maintenance  ?? false,
    can_report_breakdown:  user.permissions?.can_report_breakdown  ?? false,
    can_repair_machines:   user.permissions?.can_repair_machines   ?? false,
    can_verify_repairs:    user.permissions?.can_verify_repairs    ?? false,
    can_admin_maintenance: user.permissions?.can_admin_maintenance ?? false,
    procurement_subteams: Array.isArray(user.permissions?.procurement_subteams)
      ? user.permissions.procurement_subteams
      : ["Material", "Tooling", "Gauging"],
    client_access: {
      enabled: user.permissions?.client_access?.enabled ?? false,
      allowed_clients: user.permissions?.client_access?.allowed_clients ?? [],
      allowed_groups: user.permissions?.client_access?.allowed_groups ?? [],
    },
    can_view_stock_center: user.permissions?.can_view_stock_center ?? false,
    can_manage_bits:       user.permissions?.can_manage_bits       ?? false,
    can_sell_bits:         user.permissions?.can_sell_bits         ?? false,
    can_transfer_bits:     user.permissions?.can_transfer_bits     ?? false,
    can_view_bit_cost:     user.permissions?.can_view_bit_cost     ?? false,
    warehouse_access: {
      enabled: user.permissions?.warehouse_access?.enabled ?? false,
      allowed_warehouses: user.permissions?.warehouse_access?.allowed_warehouses ?? [],
    },
    division_access: {
      enabled: user.permissions?.division_access?.enabled ?? false,
      allowed_divisions: user.permissions?.division_access?.allowed_divisions ?? [],
    },
    can_view_scrap_sales:   user.permissions?.can_view_scrap_sales   ?? false,
    can_manage_scrap_sales: user.permissions?.can_manage_scrap_sales ?? false,
    can_view_ecotile:            user.permissions?.can_view_ecotile            ?? false,
    can_manage_ecotile_orders:   user.permissions?.can_manage_ecotile_orders   ?? false,
    can_record_ecotile_payments: user.permissions?.can_record_ecotile_payments ?? false,
    can_view_ecotile_value:      user.permissions?.can_view_ecotile_value      ?? false,
  }));
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientDropOpen, setClientDropOpen] = useState(false);
  const dropRef = useRef(null);

  // Fetch clients + client groups once
  useEffect(() => {
    axios.get(`${API}/api/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setClients(r.data || []))
      .catch(() => {});
    axios.get(`${API}/api/client-groups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setClientGroups(r.data || []))
      .catch(() => {});
  }, [token]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setClientDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const setStageAccess = (stage, level) =>
    setPerms(p => ({ ...p, stages: { ...p.stages, [stage]: level } }));

  const toggleFeature = (key) =>
    setPerms(p => ({ ...p, [key]: !p[key] }));

  const toggleSubteam = (sub) =>
    setPerms(p => {
      const cur = new Set(p.procurement_subteams || []);
      if (cur.has(sub)) cur.delete(sub); else cur.add(sub);
      return { ...p, procurement_subteams: Array.from(cur) };
    });

  const toggleClientAccess = () =>
    setPerms(p => ({ ...p, client_access: { ...p.client_access, enabled: !p.client_access.enabled } }));

  const toggleWarehouseAccess = () =>
    setPerms(p => ({ ...p, warehouse_access: { ...p.warehouse_access, enabled: !p.warehouse_access.enabled } }));

  const toggleAllowedWarehouse = (code) =>
    setPerms(p => {
      const cur = new Set(p.warehouse_access.allowed_warehouses);
      if (cur.has(code)) cur.delete(code); else cur.add(code);
      return { ...p, warehouse_access: { ...p.warehouse_access, allowed_warehouses: Array.from(cur) } };
    });

  const toggleDivisionAccess = () =>
    setPerms(p => ({ ...p, division_access: { ...p.division_access, enabled: !p.division_access.enabled } }));

  const toggleAllowedDivision = (key) =>
    setPerms(p => {
      const cur = new Set(p.division_access.allowed_divisions);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...p, division_access: { ...p.division_access, allowed_divisions: Array.from(cur) } };
    });

  const toggleAllowedClient = (name) =>
    setPerms(p => {
      const cur = new Set(p.client_access.allowed_clients);
      if (cur.has(name)) cur.delete(name); else cur.add(name);
      return { ...p, client_access: { ...p.client_access, allowed_clients: Array.from(cur) } };
    });

  const removeAllowedClient = (name) =>
    setPerms(p => ({
      ...p,
      client_access: { ...p.client_access, allowed_clients: p.client_access.allowed_clients.filter(c => c !== name) }
    }));

  const toggleAllowedGroup = (groupId) =>
    setPerms(p => {
      const cur = new Set(p.client_access.allowed_groups);
      if (cur.has(groupId)) cur.delete(groupId); else cur.add(groupId);
      return { ...p, client_access: { ...p.client_access, allowed_groups: Array.from(cur) } };
    });

  // Client IDs covered by selected groups (implicitly granted)
  const implicitClientIds = new Set(
    clientGroups
      .filter(g => (perms.client_access.allowed_groups || []).includes(g.id))
      .flatMap(g => g.client_ids || [])
  );
  // Find which group covers a given client id (for the tooltip label)
  const groupCoveringClient = (clientId) =>
    clientGroups.find(g => (perms.client_access.allowed_groups || []).includes(g.id) && (g.client_ids || []).includes(clientId));

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/users/${user.user_id}/permissions`, { permissions: perms }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Permissions updated for ${user.name}`);
      onSaved(user.user_id, perms);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[hsl(var(--card))] border-[hsl(var(--border))]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-orange-500/10 shrink-0">
              <ShieldCheck className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">{user.name}</DialogTitle>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                Role: <span className="font-medium text-[hsl(var(--foreground))]">{user.role}</span>
                <span className="ml-2 text-[hsl(var(--muted-foreground))]/60">·</span>
                <span className="ml-2">{user.email}</span>
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Stage Access */}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Stage Access</p>
            <div className="space-y-2">
              {STAGES.map(stage => (
                <div key={stage} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[hsl(var(--muted))]/20 border border-[hsl(var(--border))]/50">
                  <div className="flex items-center gap-2.5">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                    <span className="text-sm font-medium">{stage}</span>
                  </div>
                  <div className="flex rounded-lg overflow-hidden border border-[hsl(var(--border))]">
                    {ACCESS_LEVELS.map(({ value, label, Icon }) => {
                      const active = perms.stages[stage] === value;
                      return (
                        <button
                          key={value}
                          onClick={() => setStageAccess(stage, value)}
                          data-testid={`perm-stage-${stage.replace(/\s/g,'-')}-${value}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                            active
                              ? value === "edit" ? "bg-emerald-600 text-white" : value === "view" ? "bg-blue-600 text-white" : "bg-[hsl(var(--muted-foreground))]/20 text-[hsl(var(--foreground))]"
                              : "bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"
                          }`}
                        >
                          <Icon className="w-3 h-3" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Procurement Sub-team Restrictions — only meaningful for Procurement role users */}
          {user.role === "Procurement" && (
            <div data-testid="procurement-subteam-section">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1">Procurement Sub-team Access</p>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
                Limit this user to editing specific sub-teams. Leave all three enabled for unrestricted access.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "Material", color: "#a21caf" },
                  { key: "Tooling",  color: "#0891b2" },
                  { key: "Gauging",  color: "#65a30d" },
                ].map(({ key, color }) => {
                  const active = (perms.procurement_subteams || []).includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleSubteam(key)}
                      data-testid={`perm-subteam-${key.toLowerCase()}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-left transition-all ${
                        active ? "border-current bg-[var(--rl-tint)]" : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))]"
                      }`}
                      style={active ? { color, "--rl-tint": color + "12" } : {}}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${active ? "bg-current border-current" : "border-[hsl(var(--border))]"}`}>
                        {active && <Check className="w-2.5 h-2.5" style={{ color: "#fff" }} />}
                      </div>
                      <span className="text-sm font-semibold">{key}</span>
                    </button>
                  );
                })}
              </div>
              {(perms.procurement_subteams || []).length === 0 && (
                <p className="text-[11px] text-rose-500 mt-2 font-medium">
                  At least one sub-team should remain enabled — otherwise the user can't edit any procurement field.
                </p>
              )}
            </div>
          )}

          {/* Feature Permissions */}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Feature Permissions</p>
            <div className="grid grid-cols-2 gap-2">
              {FEATURE_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-feat-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-orange-500/40 bg-orange-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-orange-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Stores Permissions */}
          <div data-testid="stores-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Stores Permissions</p>
            <div className="grid grid-cols-2 gap-2">
              {STORES_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-stores-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-orange-500/40 bg-orange-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-orange-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Machine Monitor Permissions */}
          <div data-testid="machines-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Machine Monitor</p>
            <div className="grid grid-cols-2 gap-2">
              {MACHINES_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-machines-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-orange-500/40 bg-orange-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-orange-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Blocked Board Permissions */}
          <div data-testid="blocked-board-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Blocked Board</p>
            <div className="grid grid-cols-2 gap-2">
              {BLOCKED_BOARD_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-blocked-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-orange-500/40 bg-orange-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-orange-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Maintenance Permissions */}
          <div data-testid="maintenance-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Maintenance (Machine Care)</p>
            <div className="grid grid-cols-2 gap-2">
              {MAINTENANCE_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-maintenance-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-orange-500/40 bg-orange-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-orange-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Stock Center Permissions */}
          <div data-testid="stock-center-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Stock Center (Rockpecker Drilling)</p>
            <div className="grid grid-cols-2 gap-2">
              {STOCK_CENTER_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-sc-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-emerald-500/40 bg-emerald-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Ecotile Permissions (flooring division) */}
          <div data-testid="ecotile-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Ecotile (Flooring)</p>
            <div className="grid grid-cols-2 gap-2">
              {ECOTILE_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-eco-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-amber-500/40 bg-amber-500/10"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-amber-500" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Sales Permissions (scrap sales ledger) */}
          <div data-testid="sales-permissions-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-3">Sales (Scrap)</p>
            <div className="grid grid-cols-2 gap-2">
              {SALES_PERMISSIONS.map(({ key, label, description }) => {
                const active = perms[key];
                return (
                  <button
                    key={key}
                    onClick={() => toggleFeature(key)}
                    data-testid={`perm-sales-${key}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active
                        ? "border-violet-500/40 bg-violet-500/8"
                        : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors ${active ? "bg-violet-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium leading-none mb-0.5">{label}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Division Access Restriction (multi-vertical scoping) */}
          <div data-testid="division-access-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1">Division Access Restriction</p>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
              When enabled, this user only sees items, history and dashboard for the selected divisions. Replaces the old per-client workaround for division-scoped staff. Admins always see all.
            </p>

            {/* Toggle */}
            <button
              onClick={toggleDivisionAccess}
              data-testid="division-access-toggle"
              className={`flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-all mb-3 ${
                perms.division_access.enabled
                  ? "border-emerald-500/40 bg-emerald-500/8"
                  : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${perms.division_access.enabled ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                <Lock className={`w-3 h-3 ${perms.division_access.enabled ? "text-white" : "text-[hsl(var(--muted-foreground))]"}`} />
              </div>
              <div>
                <p className="text-sm font-medium leading-none mb-0.5">Limit Division Access</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {perms.division_access.enabled ? "Active — user sees only allowed divisions" : "Inactive — user sees all divisions"}
                </p>
              </div>
              <div className={`ml-auto w-9 h-5 rounded-full transition-colors shrink-0 ${perms.division_access.enabled ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/30"}`}>
                <div className={`w-3.5 h-3.5 bg-white rounded-full mt-[3px] transition-transform shadow-sm ${perms.division_access.enabled ? "translate-x-[18px]" : "translate-x-[3px]"}`} />
              </div>
            </button>

            {/* Division checkboxes — only when enabled */}
            {perms.division_access.enabled && (
              <div className="grid grid-cols-2 gap-2" data-testid="division-access-list">
                {DIVISIONS.map((d) => {
                  const selected = (perms.division_access.allowed_divisions || []).includes(d.key);
                  return (
                    <button
                      key={d.key}
                      onClick={() => toggleAllowedDivision(d.key)}
                      data-testid={`division-access-${d.key}`}
                      className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-left transition-all ${
                        selected
                          ? "border-emerald-500/40 bg-emerald-500/8"
                          : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                        {selected && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="text-sm font-medium">{d.label}</span>
                    </button>
                  );
                })}
                {(perms.division_access.allowed_divisions || []).length === 0 && (
                  <p className="col-span-2 text-[11px] text-amber-600 dark:text-amber-400">
                    No divisions selected — this user will see nothing in the pipeline.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Warehouse Access Restriction (Stock Center country scoping) */}
          <div data-testid="warehouse-access-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1">Warehouse Access Restriction</p>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
              When enabled, this user only sees the selected warehouses in the Stock Center. Admins always see all.
            </p>

            {/* Toggle */}
            <button
              onClick={toggleWarehouseAccess}
              data-testid="warehouse-access-toggle"
              className={`flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-all mb-3 ${
                perms.warehouse_access.enabled
                  ? "border-emerald-500/40 bg-emerald-500/8"
                  : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${perms.warehouse_access.enabled ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                <Lock className={`w-3 h-3 ${perms.warehouse_access.enabled ? "text-white" : "text-[hsl(var(--muted-foreground))]"}`} />
              </div>
              <div>
                <p className="text-sm font-medium leading-none mb-0.5">Limit Warehouse Access</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {perms.warehouse_access.enabled ? "Active — user sees only allowed warehouses" : "Inactive — user sees all warehouses"}
                </p>
              </div>
              <div className={`ml-auto w-9 h-5 rounded-full transition-colors shrink-0 ${perms.warehouse_access.enabled ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/30"}`}>
                <div className={`w-3.5 h-3.5 bg-white rounded-full mt-[3px] transition-transform shadow-sm ${perms.warehouse_access.enabled ? "translate-x-[18px]" : "translate-x-[3px]"}`} />
              </div>
            </button>

            {/* Country checkboxes — only when enabled */}
            {perms.warehouse_access.enabled && (
              <div className="grid grid-cols-2 gap-2" data-testid="warehouse-access-list">
                {WAREHOUSES.map((w) => {
                  const selected = (perms.warehouse_access.allowed_warehouses || []).includes(w.code);
                  return (
                    <button
                      key={w.code}
                      onClick={() => toggleAllowedWarehouse(w.code)}
                      data-testid={`warehouse-access-${w.code}`}
                      className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-left transition-all ${
                        selected
                          ? "border-emerald-500/40 bg-emerald-500/8"
                          : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-emerald-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                        {selected && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="text-sm font-medium">{w.name}</span>
                      <span className="ml-auto text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{w.code}</span>
                    </button>
                  );
                })}
                {(perms.warehouse_access.allowed_warehouses || []).length === 0 && (
                  <p className="col-span-2 text-[11px] text-amber-600 dark:text-amber-400">
                    No warehouses selected — this user will see nothing in the Stock Center.
                  </p>
                )}
              </div>
            )}
          </div>
          {/* Client Access Restriction */}
          <div data-testid="client-access-section">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1">Client Access Restriction</p>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
              When enabled, this user only sees items and history entries belonging to the selected clients or groups.
            </p>

            {/* Toggle */}
            <button
              onClick={toggleClientAccess}
              data-testid="client-access-toggle"
              className={`flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-all mb-3 ${
                perms.client_access.enabled
                  ? "border-rose-500/40 bg-rose-500/8"
                  : "border-[hsl(var(--border))]/50 bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${perms.client_access.enabled ? "bg-rose-600" : "bg-[hsl(var(--muted-foreground))]/20"}`}>
                <Lock className={`w-3 h-3 ${perms.client_access.enabled ? "text-white" : "text-[hsl(var(--muted-foreground))]"}`} />
              </div>
              <div>
                <p className="text-sm font-medium leading-none mb-0.5">Limit Client Access</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  {perms.client_access.enabled ? "Active — user sees only allowed clients" : "Inactive — user sees all clients"}
                </p>
              </div>
              <div className={`ml-auto w-9 h-5 rounded-full transition-colors shrink-0 ${perms.client_access.enabled ? "bg-rose-600" : "bg-[hsl(var(--muted-foreground))]/30"}`}>
                <div className={`w-3.5 h-3.5 bg-white rounded-full mt-[3px] transition-transform shadow-sm ${perms.client_access.enabled ? "translate-x-[18px]" : "translate-x-[3px]"}`} />
              </div>
            </button>

            {/* Selector — only when enabled */}
            {perms.client_access.enabled && (
              <div className="space-y-3">

                {/* ── Group shortcuts ── */}
                {clientGroups.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Layers className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-[hsl(var(--muted-foreground))]">
                        Client Groups — auto-includes future members
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {clientGroups.map(group => {
                        const selected = (perms.client_access.allowed_groups || []).includes(group.id);
                        return (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => toggleAllowedGroup(group.id)}
                            data-testid={`group-access-${group.id}`}
                            className={`inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                              selected
                                ? "opacity-100"
                                : "opacity-60 hover:opacity-90 bg-[hsl(var(--muted))]/30"
                            }`}
                            style={selected ? {
                              background: group.color + "22",
                              borderColor: group.color + "70",
                              color: group.color,
                            } : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }}
                          >
                            <div
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ background: selected ? group.color : "hsl(var(--muted-foreground))" }}
                            />
                            {group.name}
                            {selected && (
                              <span className="text-[9px] ml-0.5 opacity-70">
                                {(group.client_ids || []).length}
                              </span>
                            )}
                            {selected && <X className="w-2.5 h-2.5 ml-0.5" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Selected tags (groups + individual clients) ── */}
                {((perms.client_access.allowed_groups || []).length > 0 || perms.client_access.allowed_clients.length > 0) && (
                  <div className="flex flex-wrap gap-1.5">
                    {/* Explicit individual client tags */}
                    {perms.client_access.allowed_clients.map(name => {
                      const cl = clients.find(c => c.name === name);
                      return (
                        <span
                          key={name}
                          className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] font-medium border"
                          style={{
                            background: (cl?.color || "#f97316") + "18",
                            borderColor: (cl?.color || "#f97316") + "50",
                            color: cl?.color || "#f97316",
                          }}
                          onMouseDown={(e) => {
                            if (e.button === 1) {
                              e.preventDefault();
                              removeAllowedClient(name);
                            }
                          }}
                        >
                          {name}
                          <button
                            type="button"
                            onClick={() => removeAllowedClient(name)}
                            className="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-black/10 transition-colors"
                            data-testid={`remove-client-${name}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* ── Individual client dropdown ── */}
                <div className="relative" ref={dropRef}>
                  <button
                    type="button"
                    onClick={() => setClientDropOpen(o => !o)}
                    data-testid="client-select-trigger"
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 hover:bg-[hsl(var(--muted))]/40 text-left text-sm transition-colors"
                  >
                    <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] shrink-0" />
                    <span className="flex-1 text-[hsl(var(--muted-foreground))] text-[12px]">
                      {perms.client_access.allowed_clients.length > 0
                        ? `${perms.client_access.allowed_clients.length} individual client${perms.client_access.allowed_clients.length > 1 ? "s" : ""} — click to add more`
                        : "Add individual clients…"}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] transition-transform ${clientDropOpen ? "rotate-180" : ""}`} />
                  </button>

                  {clientDropOpen && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg overflow-hidden">
                      <div className="p-2 border-b border-[hsl(var(--border))]/50">
                        <input
                          autoFocus
                          type="text"
                          value={clientSearch}
                          onChange={e => setClientSearch(e.target.value)}
                          placeholder="Type to search clients…"
                          className="w-full px-2.5 py-1.5 text-[12px] rounded-md border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/30 outline-none placeholder:text-[hsl(var(--muted-foreground))]/60 focus:border-rose-400/60"
                          data-testid="client-search-input"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto py-1">
                        {filteredClients.length === 0 ? (
                          <p className="text-[11px] text-[hsl(var(--muted-foreground))] px-3 py-2">No clients match</p>
                        ) : (
                          filteredClients.map(cl => {
                            const explicitlySelected = perms.client_access.allowed_clients.includes(cl.name);
                            const implicitGroup = groupCoveringClient(cl.id);
                            const isImplicit = !!implicitGroup;

                            return (
                              <button
                                key={cl.id}
                                type="button"
                                onClick={() => { if (!isImplicit) toggleAllowedClient(cl.name); }}
                                data-testid={`client-option-${cl.id}`}
                                className={`flex items-center gap-2.5 w-full px-3 py-2 text-left text-[12px] transition-colors ${
                                  isImplicit
                                    ? "opacity-50 cursor-not-allowed"
                                    : "hover:bg-[hsl(var(--muted))]/40 cursor-pointer"
                                }`}
                              >
                                <div
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors`}
                                  style={
                                    isImplicit
                                      ? { background: cl.color + "50", borderColor: cl.color + "50" }
                                      : explicitlySelected
                                        ? { background: cl.color, borderColor: cl.color }
                                        : { borderColor: cl.color + "80" }
                                  }
                                >
                                  {(explicitlySelected || isImplicit) && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                                <span
                                  className="w-2.5 h-2.5 rounded-full shrink-0"
                                  style={{ background: cl.color }}
                                />
                                <span className={`flex-1 ${explicitlySelected ? "font-medium" : ""}`}>{cl.name}</span>
                                {isImplicit && (
                                  <span
                                    className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                                    style={{ background: implicitGroup.color + "20", color: implicitGroup.color }}
                                  >
                                    via {implicitGroup.name}
                                  </span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {perms.client_access.enabled
                  && (perms.client_access.allowed_clients.length === 0)
                  && ((perms.client_access.allowed_groups || []).length === 0) && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium" data-testid="client-access-empty-warn">
                    No clients or groups selected — user will see no items until at least one is allowed.
                  </p>
                )}
              </div>
            )}
          </div>

        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[hsl(var(--border))]">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-permissions-btn"
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Save Permissions
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
